import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import NotificationBell from "./NotificationBell";
import MemberMenu from "./MemberMenu";
import ThemeToggle from "./ThemeToggle";
import SpotlightSearch from "./spotlight/SpotlightSearch";
import { useBrand } from "./useBrand";
import { themeSwitchEnabled, useThemeMode } from "./theme";
import { isPlatformDefaultLogo } from "./brandDefaultLogo";
import BrandDefaultMark from "./BrandDefaultMark";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

/**
 * The single top bar for the signed-in membership app.
 *
 * This used to exist twice — once on the membership home (the groups list) and
 * once inside a group — and the two copies had to be kept in sync by hand. They
 * drifted anyway, and which one you saw depended on whether the membership
 * happened to have a group. Both callers now render this.
 *
 * Everything that differed between the two is a prop:
 *   - `showBack` / `onBack`  — only inside a group is there anything to go back to
 *   - `switcher`             — the group switcher, feed-only
 *   - `mobileNav`            — the in-group compact nav (the home has none)
 *   - `navClass`            — where the inline nav appears (or is suppressed)
 *   - `onSignIn`            — the home renders nothing for guests; the feed offers Sign In
 */

/**
 * The membership's own logo, falling back to the themed platform placeholder.
 *
 * No FlexiMemberships mark here: this renders on the CUSTOMER's membership, so
 * our branding must never appear on it. If even the placeholder fails to load,
 * the membership's own name is shown rather than anything of ours.
 * Shared so the header and the Studio sidebar can't drift apart.
 *
 * When no logo is set, the placeholder is drawn INLINE rather than fetched as
 * an image, so it can follow both the theme and this membership's own brand
 * colour. The old default was one PNG with an opaque background, which showed
 * as a bright slab on a dark membership. See BrandDefaultMark.
 */
export function BrandLogo({ community, onClick, className = "h-8 w-auto max-w-[130px]" }) {
  const logo = community?.logo;
  const isDefault = isPlatformDefaultLogo(logo);
  const src = isDefault ? "" : String(logo).trim();
  const [failed, setFailed] = useState(false);

  // Reset the error latch when the logo changes, or a single broken URL would
  // permanently suppress a later, working one. Declared before any branch —
  // hooks must run in the same order on every render.
  useEffect(() => setFailed(false), [src]);

  // No logo of their own: render the mark INLINE rather than as an image, so it
  // can take the theme's text colour and this membership's brand colour. See
  // BrandDefaultMark.
  if (isDefault) {
    return (
      <BrandDefaultMark community={community} className={className} onClick={onClick} />
    );
  }

  if (!failed) {
    return (
      <img
        src={src}
        alt={community?.name || ""}
        onClick={onClick}
        className={`${className} object-contain ${onClick ? "cursor-pointer" : ""}`}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      onClick={onClick}
      className={`text-base font-extrabold leading-none tracking-tight text-[#1A1B22] dark:text-gray-100 ${
        onClick ? "cursor-pointer" : ""
      }`}
    >
      {community?.name || ""}
    </span>
  );
}

/**
 * The compact nav for narrow screens: a single pill showing the current section
 * that opens the full list. Lives inside the header, so the bar stays one row.
 *
 * The membership home used to render a separate pill-strip row *under* its
 * header instead — which is what read as a second header on mobile, and why the
 * group page looked unlike every other page.
 *
 * `items` are the tabs to offer; `activeKey`/`isActive` decide what's current.
 */
export function CompactNavMenu({ items = [], activeKey, isActive, onNavigate, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);

  const active = (key) => (isActive ? isActive(key) : activeKey === key);
  if (!items.length) return null;
  const current = items.find((t) => active(t.key)) || items[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 max-w-[42vw] sm:max-w-[40vw] px-2.5 sm:px-3.5 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-800 transition"
      >
        <span className="shrink-0" style={{ color: primary }}>{current.icon}</span>
        <span className="truncate hidden min-[400px]:inline">{current.label}</span>
        <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-40 w-60 bg-surface rounded-2xl border border-gray-200 shadow-xl ring-1 ring-black/5 p-1.5 max-h-[70vh] overflow-y-auto max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:top-[68px] max-sm:mt-0 max-sm:w-auto max-sm:translate-x-0">
          {items.map((t) => {
            const on = active(t.key);
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setOpen(false); onNavigate?.(t.key); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-semibold transition"
                style={on ? { background: `${primary}0f`, color: primary } : { color: "rgb(var(--c-gray-700))" }}
              >
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={on ? { background: `${primary}1a`, color: primary } : { background: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
                >
                  {t.icon}
                </span>
                <span className="flex-1 text-left truncate">{t.label}</span>
                {on && (
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "More ▾" overflow dropdown. Renders nothing when there's nothing to overflow. */
export function MoreMenu({ items = [], activeKey, isActive, onSelect, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);

  // `isActive`, when given, overrides the plain key match — e.g. a custom
  // menu's key is "page:<id>" while the view is "page".
  const matches = (key) => (isActive ? isActive(key) : activeKey === key);
  const anyActive = items.some((t) => matches(t.key));
  if (!items.length) return null;

  return (
    <div className="relative h-full flex items-center" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-xl px-3 sm:px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${anyActive || open ? "" : "text-gray-500"}`}
        style={anyActive || open ? { backgroundColor: `${primary}14`, color: primary } : undefined}
        onMouseEnter={anyActive || open ? undefined : (e) => { e.currentTarget.style.background = `${primary}0f`; e.currentTarget.style.color = primary; }}
        onMouseLeave={anyActive || open ? undefined : (e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = ""; }}
      >
        More
        <svg className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full end-0 mt-1 z-40 w-52 bg-surface rounded-2xl border border-gray-200 shadow-xl ring-1 ring-black/5 p-1.5">
          {items.map((t) => {
            const active = matches(t.key);
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => { setOpen(false); onSelect?.(t.key); }}
                className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={active ? { background: `${primary}0f`, color: primary } : { color: "rgb(var(--c-gray-700))" }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = `${primary}0f`; e.currentTarget.style.color = primary; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = ""; e.currentTarget.style.color = "rgb(var(--c-gray-700))"; } }}
              >
                <span className="shrink-0" style={{ color: active ? primary : "rgb(var(--c-gray-500))" }}>{t.icon}</span>
                <span className="flex-1 text-left truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Avatar (member image or initial) used inside the mobile drawer. */
function DrawerAvatar({ member, primary, size }) {
  const img = member?.img;
  const initial = (member?.name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white"
      style={{ backgroundColor: primary, width: size, height: size, fontSize: size * 0.4 }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
}

/**
 * Full-screen slide-in drawer for the Catalog mobile header (Udemy-style).
 * Screen 1: the signed-in user (tap → account) + the section nav list.
 * Screen 2: the account actions (Profile / Change password / Account settings /
 * My Plans / Sign Out), reached by tapping the user and dismissed with "Menu".
 */
function MobileNavDrawer({
  open, onClose, community, member, primary, tabs, tabActive, onNavigate,
  onProfile, onChangePassword, onAccountSettings, onMyPlans, onMobileCode, onSignOut, onJoin, onSignIn, guest,
  // The account page currently open (Spotlight's Profile / Change password /
  // My Plans pages) — its row is lit like the current nav item.
  activePage = null,
}) {
  const [screen, setScreen] = useState("main");
  // Always reopen on the main screen. Body-scroll locking is handled globally by
  // <ScrollLockManager/> (it detects this backdrop as an overlay while open) — we
  // must NOT also set body.style.overflow here, or the two fight and the page can
  // get stuck frozen after the drawer closes (the manager captures our value).
  useEffect(() => { if (open) setScreen("main"); }, [open]);

  const navItem = (t) => {
    const on = tabActive(t.key);
    return (
      <button
        key={t.key}
        type="button"
        onClick={() => { onNavigate?.(t.key); onClose(); }}
        className="flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] font-semibold transition"
        style={on ? { color: primary, backgroundColor: `${primary}0f` } : { color: "rgb(var(--c-gray-700))" }}
      >
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={on ? { backgroundColor: `${primary}1a`, color: primary } : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
        >
          {t.icon}
        </span>
        <span className="flex-1 truncate">{t.label}</span>
      </button>
    );
  };

  const acctItem = (label, iconPath, onClick, danger, key) => {
    const on = !!key && key === activePage;
    return (
    <button
      type="button"
      onClick={() => { onClose(); onClick?.(); }}
      aria-current={on ? "page" : undefined}
      // The danger branch is a fixed red tint, so it goes through classes (which
      // can carry a dark twin); the neutral branch stays inline on the ramp.
      className={`flex w-full items-center gap-3 px-4 py-3 text-left text-[15px] font-semibold transition active:bg-black/[0.03] ${
        danger ? "text-red-600 dark:text-red-300" : ""
      }`}
      style={danger ? undefined : on ? { color: primary, backgroundColor: `${primary}0f` } : { color: "rgb(var(--c-gray-700))" }}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          danger ? "bg-red-50 dark:bg-red-500/15 text-red-500 dark:text-red-300" : ""
        }`}
        style={danger ? undefined : on ? { backgroundColor: `${primary}1a`, color: primary } : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
      >
        <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </span>
      {label}
    </button>
    );
  };

  return createPortal(
    <div className="lg:hidden" aria-hidden={!open}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-[60] bg-ink-slate/40 backdrop-blur-[1px] transition-opacity duration-300 ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      {/* Close button, floating just past the drawer's right edge (Udemy style). */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        className={`fixed top-3 z-[62] flex h-10 w-10 items-center justify-center rounded-full bg-surface text-gray-600 shadow-md transition-opacity duration-300 ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
        style={{ left: "calc(min(86vw, 340px) + 12px)" }}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      {/* Panel */}
      <aside
        className={`fixed inset-y-0 start-0 z-[61] flex w-[86vw] max-w-[340px] flex-col bg-surface shadow-2xl transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"}`}
      >
        {screen === "main" ? (
          <>
            {member ? (
              <button
                type="button"
                onClick={() => setScreen("account")}
                className="flex items-center gap-3 border-b border-gray-100 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-left transition hover:bg-gray-50"
              >
                <DrawerAvatar member={member} primary={primary} size={46} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-extrabold text-gray-900">Hi, {member.name || "Member"}</span>
                  <span className="block text-[12px] text-gray-400">Welcome back</span>
                </span>
                <svg className="h-5 w-5 shrink-0 text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="border-b border-gray-100 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))]">
                <BrandLogo community={community} />
                <button
                  onClick={() => { onClose(); (guest === "join" ? onJoin : onSignIn)?.(); }}
                  className="mt-3 w-full rounded-full py-2.5 text-sm font-bold text-white shadow"
                  style={{ backgroundColor: primary }}
                >
                  {guest === "join" ? "Join" : "Sign In"}
                </button>
              </div>
            )}
            <nav className="min-h-0 flex-1 overflow-y-auto py-2 pb-[env(safe-area-inset-bottom)]">
              {tabs.map(navItem)}
            </nav>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setScreen("main")}
              className="flex items-center gap-2 border-b border-gray-100 px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-[15px] font-bold text-gray-800"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Menu
            </button>
            <div className="flex items-center gap-3 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-surface px-4 py-3.5">
              <DrawerAvatar member={member} primary={primary} size={44} />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-gray-900">{member?.name || "Member"}</p>
                {member?.email && <p className="truncate text-xs text-gray-400">{member.email}</p>}
              </div>
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto py-2 pb-[env(safe-area-inset-bottom)]">
              {acctItem("Profile", "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z", onProfile, false, "profile")}
              {acctItem("Change password", "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", onChangePassword, false, "password")}
              {acctItem("Account settings", "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z", onAccountSettings)}
              {acctItem("My Plans", "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4", onMyPlans, false, "plans")}
              {acctItem("Mobile Code", "M9.5 2.5h5A2.5 2.5 0 0117 5v14a2.5 2.5 0 01-2.5 2.5h-5A2.5 2.5 0 017 19V5a2.5 2.5 0 012.5-2.5zM10.5 18.5h3", onMobileCode)}
              <div className="my-1 border-t border-gray-100" />
              {acctItem("Sign Out", "M15 12H3m0 0l4-4m-4 4l4 4M21 4v16a1 1 0 01-1 1h-6m7-18a1 1 0 00-1-1h-6", onSignOut, true)}
            </nav>
          </>
        )}
      </aside>
    </div>,
    document.body
  );
}

/**
 * Full-screen mobile menu for the Campus template (Teachable-style). One screen:
 * the signed-in user on top, the whole section nav, then the account actions —
 * fades / slides in over the page.
 */
function CampusMobileMenu({
  open, onClose, community, member, primary, tabs, tabActive, onNavigate,
  onProfile, onChangePassword, onAccountSettings, onMyPlans, onMobileCode, onSignOut, onJoin, onSignIn, guest,
}) {
  const navItem = (t) => {
    const on = tabActive(t.key);
    return (
      <button
        key={t.key}
        type="button"
        onClick={() => { onNavigate?.(t.key); onClose(); }}
        className="flex w-full items-center gap-3 px-5 py-3 text-left text-[15px] font-semibold transition"
        style={on ? { color: primary, backgroundColor: `${primary}0f` } : { color: "rgb(var(--c-gray-700))" }}
      >
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={on ? { backgroundColor: `${primary}1a`, color: primary } : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
        >
          {t.icon}
        </span>
        <span className="flex-1 truncate">{t.label}</span>
      </button>
    );
  };

  const acctItem = (label, iconPath, onClick, danger) => (
    <button
      type="button"
      onClick={() => { onClose(); onClick?.(); }}
      // The danger branch is a fixed red tint, so it goes through classes (which
      // can carry a dark twin); the neutral branch stays inline on the ramp.
      className={`flex w-full items-center gap-3 px-5 py-3 text-left text-[15px] font-semibold transition active:bg-black/[0.03] ${
        danger ? "text-red-600 dark:text-red-300" : ""
      }`}
      style={danger ? undefined : { color: "rgb(var(--c-gray-700))" }}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          danger ? "bg-red-50 dark:bg-red-500/15 text-red-500 dark:text-red-300" : ""
        }`}
        style={danger ? undefined : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
      >
        <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </span>
      {label}
    </button>
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] flex flex-col bg-surface transition-all duration-300 ease-out lg:hidden ${open ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"}`}
      aria-hidden={!open}
    >
      {/* Top row — logo + close */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 pb-2.5 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <BrandLogo community={community} />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close menu"
          className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 active:scale-95"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
        {/* User / guest header */}
        {member ? (
          <div className="flex items-center gap-3 px-5 py-4">
            <DrawerAvatar member={member} primary={primary} size={48} />
            <div className="min-w-0">
              <p className="truncate text-[16px] font-extrabold text-gray-900">{member.name || "Member"}</p>
              <p className="text-[12px] text-gray-400">Welcome back</p>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4">
            <button
              onClick={() => { onClose(); (guest === "join" ? onJoin : onSignIn)?.(); }}
              className="w-full rounded-full py-3 text-sm font-bold text-white shadow"
              style={{ backgroundColor: primary }}
            >
              {guest === "join" ? "Join" : "Sign In"}
            </button>
          </div>
        )}

        <div className="border-t border-gray-100" />
        <nav className="py-2">{tabs.map(navItem)}</nav>

        {member && (
          <>
            <div className="border-t border-gray-100" />
            <nav className="py-2">
              {acctItem("Profile", "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z", onProfile)}
              {acctItem("Change password", "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z", onChangePassword)}
              {acctItem("Account settings", "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z", onAccountSettings)}
              {acctItem("My Plans", "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4", onMyPlans)}
              {acctItem("Mobile Code", "M9.5 2.5h5A2.5 2.5 0 0117 5v14a2.5 2.5 0 01-2.5 2.5h-5A2.5 2.5 0 017 19V5a2.5 2.5 0 012.5-2.5zM10.5 18.5h3", onMobileCode)}
              <div className="my-1 border-t border-gray-100" />
              {acctItem("Sign Out", "M15 12H3m0 0l4-4m-4 4l4 4M21 4v16a1 1 0 01-1 1h-6m7-18a1 1 0 00-1-1h-6", onSignOut, true)}
            </nav>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Per-template chrome. Each template keeps its own bar (that's the point of
 * templates) — but the bar is defined ONCE here and rendered by both the
 * membership home and the in-group shell, so a section can't get a different
 * header just because a different file drew it.
 *
 *   shell   — the <header> element's own classes
 *   bar     — the top row (container width + height)
 *   nav     — where the section nav lives:
 *               "inline" tabs in the bar (Classic)
 *               "sub"    a pill row under the bar (Catalog / Campus)
 *               "none"   somewhere else entirely (Studio sidebar, Creator bottom nav)
 *   subMax  — container width for the "sub" row
 */
const CHROME = {
  classic: {
    shell: "bg-surface/85 backdrop-blur-md border-b border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sticky top-0 z-30",
    bar: "px-3 sm:px-6 h-16 flex items-center gap-2 sm:gap-3 lg:gap-4",
    nav: "inline",
    guest: "signin",
  },
  studio: {
    shell: "bg-surface/85 backdrop-blur-md border-b border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sticky top-0 z-30",
    bar: "px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-3 lg:gap-4",
    nav: "none",
    guest: "signin",
  },
  catalog: {
    shell: "sticky top-0 z-30 border-b border-gray-100 bg-surface/90 backdrop-blur-md",
    bar: "mx-auto flex min-h-16 max-w-[1200px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 sm:flex-nowrap sm:px-6 sm:py-0",
    nav: "sub",
    subMax: "max-w-[1200px]",
    guest: "join",
  },
  campus: {
    shell: "sticky top-0 z-30 border-b border-gray-100 bg-surface/90 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
    bar: "mx-auto flex h-16 max-w-[1180px] items-center gap-3 px-4 sm:px-6",
    nav: "sub",
    subMax: "max-w-[1180px]",
    guest: "join",
  },
  // Spotlight: a slim bar beside the full-height sidebar (which carries the
  // logo and the nav), holding the search and the account actions.
  spotlight: {
    shell: "sticky top-0 z-30 border-b border-gray-200 bg-surface/95 backdrop-blur-md",
    bar: "px-4 sm:px-6 h-14 flex items-center gap-3",
    nav: "none",
    guest: "join",
  },
  creator: {
    shell: "sticky top-0 z-30 border-b border-gray-100 bg-surface/90 backdrop-blur-md",
    // The nav lives in the collapsible left sidebar (TagMango-style); the bar only
    // carries the logo (mobile) and the right-hand actions.
    bar: "px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-3 lg:gap-4",
    nav: "none",
    guest: "join",
  },
};

export default function MembershipHeader({
  template = "classic",
  community,
  token,
  member,
  primary = "#0274F7",
  tabs = [],
  moreItems = [],
  activeKey,
  onNavigate,
  isActive,
  showBack = false,
  onBack,
  onLogoClick,
  switcher = null,
  mobileNav = null,
  navClass = "hidden lg:flex",
  // Studio puts the logo in its sidebar, so the bar only needs it on mobile.
  // A wrapper, not a class on the logo itself: the brand fallback renders its
  // own element and would drop a className meant for the <img> variant.
  logoWrapClass = "",
  // Extra bar content for templates that carry it (Catalog's group search +
  // filter). Only the page that owns that state passes it.
  extra = null,
  onJoin,
  onProfile,
  onChangePassword,
  onAccountSettings,
  onMyPlans,
  onMobileCode,
  onSignOut,
  onSignIn,
}) {
  // Most tabs are active when they match activeKey, but a caller can override
  // (in-group, "Groups" stays lit while the feed — which has no tab — is open).
  const tabActive = (key) => (isActive ? isActive(key) : activeKey === key);
  const chrome = CHROME[template] || CHROME.classic;
  const navigate = useNavigate();
  const allTabs = [...tabs, ...moreItems];
  // Catalog + Campus get an app-style mobile header: a menu icon that opens a
  // full menu (nav + account), replacing the wrapped pill row on phones.
  //   Catalog → Udemy-style left slide-in drawer (logo centred, hamburger left).
  //   Campus  → Teachable-style full-screen overlay (logo left, menu icon right).
  const isCatalogTpl = template === "catalog";
  const isCampusTpl = template === "campus";
  const isCreatorTpl = template === "creator";
  const isSpotlightTpl = template === "spotlight";
  // Templates with the hamburger phone bar + slide-in drawer.
  const drawerTpl = isCatalogTpl || isCreatorTpl || isSpotlightTpl;
  const mobileMenuTpl = drawerTpl || isCampusTpl;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Light/dark. The bar reads the membership's brand itself rather than taking
  // the mode as a prop: useBrand is the cached, shared getfunnel call every
  // other surface already makes, and threading a mode through both shells (and
  // every page that renders them) to reach one icon would be the longer wire.
  const { brand } = useBrand(token);
  const [themeMode, changeThemeMode] = useThemeMode(token, brand);
  // No control unless the owner opted in — otherwise their chosen appearance is
  // the only one, and a switch would suggest otherwise.
  const themeControl = themeSwitchEnabled(brand) ? (
    <ThemeToggle mode={themeMode} onChange={changeThemeMode} primary={primary} />
  ) : null;

  const tabBtn = (t, cls) => {
    const active = tabActive(t.key);
    return (
      <button
        key={t.key}
        onClick={() => onNavigate?.(t.key)}
        className={cls}
        style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
        onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.background = `${primary}0f`; e.currentTarget.style.color = primary; }}
        onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = ""; }}
      >
        {t.icon}
        {t.label}
      </button>
    );
  };

  // Campus nav is an underline tab strip (learning-platform style) — no pill
  // fill; the active tab is marked by a brand-colored underline + brand text,
  // and hovering a tab tints its text with the brand color. This keeps Campus
  // visually distinct from Catalog, which uses the same "sub" row as rounded pills.
  const campusTabBtn = (t) => {
    const active = tabActive(t.key);
    return (
      <button
        key={t.key}
        onClick={() => onNavigate?.(t.key)}
        className={`group relative flex h-full shrink-0 items-center gap-2 text-sm font-semibold transition-colors ${active ? "" : "text-gray-500"}`}
        style={active ? { color: primary } : undefined}
        onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.color = primary; }}
        onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.color = ""; }}
      >
        <span className="shrink-0 [&_svg]:h-[18px] [&_svg]:w-[18px]">{t.icon}</span>
        {t.label}
        {/* Brand underline — solid when active, a faint fade-in on hover. */}
        <span
          className={`pointer-events-none absolute inset-x-0 -bottom-px h-[3px] rounded-full transition-opacity duration-200 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-40"}`}
          style={{ backgroundColor: primary }}
        />
      </button>
    );
  };

  const profile = member ? (
    <>
      <NotificationBell token={token} member={member} primary={primary} />
      <MemberMenu
        member={member}
        token={token}
        primary={primary}
        // Lights Profile / Change password / My Plans while that page is open
        // (Spotlight's account pages; other sections match none of them).
        activePage={activeKey}
        onProfile={onProfile}
        onChangePassword={onChangePassword}
        onAccountSettings={onAccountSettings}
        onMyPlans={onMyPlans}
        onMobileCode={onMobileCode}
        onSignOut={onSignOut}
      />
    </>
  ) : chrome.guest === "join" && onJoin ? (
    <button
      onClick={onJoin}
      style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
      className="rounded-full px-5 py-2 text-sm font-bold text-white shadow transition hover:opacity-90"
    >
      Join
    </button>
  ) : onSignIn ? (
    <button
      onClick={onSignIn}
      style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
      className="text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 transition active:scale-95"
    >
      Sign In
    </button>
  ) : null;

  return (
    <header className={chrome.shell}>
      {/* Catalog phone bar — hamburger (left), centred logo, notifications (right).
          The full bar + sub-nav below are hidden here and shown from lg up. */}
      {drawerTpl && (
        <div className="flex h-14 items-center gap-2 px-3 lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-600 transition hover:bg-gray-100 active:scale-95"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* The switcher rides beside the logo here exactly as it does in the
              desktop bar. Without it, switching groups on a phone was possible
              in Classic and Studio only — the two templates whose single bar is
              never swapped for one of these phone bars. */}
          <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
            <BrandLogo community={community} onClick={onLogoClick} />
            {switcher}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {/* Spotlight's search lives in the desktop bar; on phones this opens
                the Search page, which carries its own box. */}
            {isSpotlightTpl && member ? (
              <button
                type="button"
                onClick={() => navigate(`/membership/${token}/search`)}
                aria-label="Search"
                className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-gray-100"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
              </button>
            ) : null}
            {themeControl}
            {member ? <NotificationBell token={token} member={member} primary={primary} /> : profile}
          </div>
        </div>
      )}

      {/* Campus phone bar (Teachable-style) — logo left, notifications + menu right. */}
      {isCampusTpl && (
        <div className="flex h-14 items-center gap-2 px-4 lg:hidden">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <BrandLogo community={community} onClick={onLogoClick} />
            {switcher}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {themeControl}
            {member ? <NotificationBell token={token} member={member} primary={primary} /> : profile}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-700 transition hover:bg-gray-100 active:scale-95"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {!isCampusTpl && (
      <div className={`${chrome.bar}${mobileMenuTpl ? " hidden lg:flex" : ""}`}>
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 shrink-0">
          {showBack && (
            <button onClick={onBack} aria-label="Back to community" className="text-gray-500 hover:text-gray-900">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <span className={logoWrapClass}><BrandLogo community={community} onClick={onLogoClick} /></span>

          {switcher}
        </div>

        {extra}

        {/* Spotlight's search sits where the other bars put their nav. */}
        {isSpotlightTpl ? (
          <div className="min-w-0 flex-1">
            <SpotlightSearch token={token} primary={primary} />
          </div>
        ) : null}

        {/* Ignored when the nav lives in a sub-row: that row already scrolls on
            narrow screens, so a compact menu beside it would be the same nav twice. */}
        {mobileNav && chrome.nav !== "sub" && (
          <div className="lg:hidden flex-1 min-w-0 flex justify-center">{mobileNav}</div>
        )}

        {chrome.nav === "inline" && (
          <nav className={`${navClass} flex-1 min-w-0 items-center justify-center gap-1 h-full`}>
            {tabs.map((t) =>
              tabBtn(
                t,
                `flex items-center gap-1.5 rounded-xl px-3 sm:px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${tabActive(t.key) ? "" : "text-gray-500"}`
              )
            )}
            <MoreMenu items={moreItems} activeKey={activeKey} isActive={isActive} onSelect={onNavigate} primary={primary} />
          </nav>
        )}

        <div className="shrink-0 ms-auto flex items-center gap-1.5">
          {themeControl}
          {profile}
          {/* Spotlight names the member beside their avatar, as a club does. */}
          {isSpotlightTpl && member?.name ? (
            <span className="hidden max-w-[10rem] truncate ps-1 text-[13.5px] font-semibold text-gray-800 xl:inline">{member.name.split(" ")[0]}</span>
          ) : null}
        </div>
      </div>
      )}

      {/* Campus desktop bar — a single-row LMS topbar (distinct from Catalog's
          two-row pill header): logo, a hairline divider, then the section nav as a
          left-grouped underline strip that runs the full header height so the active
          underline hugs the bottom edge, and the account actions pinned right. */}
      {isCampusTpl && (
        <div className={`${chrome.bar} hidden lg:flex`}>
          <div className="flex h-full items-center gap-3 min-w-0 shrink-0">
            {showBack && (
              <button onClick={onBack} aria-label="Back to community" className="text-gray-500 transition hover:text-gray-900">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <span className={logoWrapClass}><BrandLogo community={community} onClick={onLogoClick} /></span>
            {switcher}
            <span className="h-6 w-px shrink-0 bg-gray-200" aria-hidden="true" />
          </div>

          <nav className="flex h-full min-w-0 flex-1 items-center gap-6">
            {tabs.map((t) => campusTabBtn(t))}
            <MoreMenu items={moreItems} activeKey={activeKey} isActive={isActive} onSelect={onNavigate} primary={primary} />
          </nav>

          {extra}

          <div className="ml-auto flex shrink-0 items-center gap-1.5">{themeControl}{profile}</div>
        </div>
      )}

      {/* Catalog puts the section nav in a row under the bar as rounded pills.
          Primary tabs sit inline and the overflow folds into a "More" dropdown, so
          the row never needs a horizontal scrollbar (nor clips the dropdown). */}
      {chrome.nav === "sub" && !isCampusTpl && allTabs.length > 0 && (
        <div className={`border-t border-gray-100${mobileMenuTpl ? " hidden lg:block" : ""}`}>
          <div className={`mx-auto ${chrome.subMax} px-4 py-2 sm:px-6`}>
            <div className="flex flex-wrap items-center gap-1.5">
              {tabs.map((t) =>
                tabBtn(
                  t,
                  `shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${tabActive(t.key) ? "" : "text-gray-500"}`
                )
              )}
              <MoreMenu items={moreItems} activeKey={activeKey} isActive={isActive} onSelect={onNavigate} primary={primary} />
            </div>
          </div>
        </div>
      )}

      {drawerTpl && (
        <MobileNavDrawer
          activePage={activeKey}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          community={community}
          member={member}
          primary={primary}
          tabs={allTabs}
          tabActive={tabActive}
          onNavigate={onNavigate}
          onProfile={onProfile}
          onChangePassword={onChangePassword}
          onAccountSettings={onAccountSettings}
          onMyPlans={onMyPlans}
          onMobileCode={onMobileCode}
          onSignOut={onSignOut}
          onJoin={onJoin}
          onSignIn={onSignIn}
          guest={chrome.guest}
        />
      )}

      {isCampusTpl && (
        <CampusMobileMenu
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          community={community}
          member={member}
          primary={primary}
          tabs={allTabs}
          tabActive={tabActive}
          onNavigate={onNavigate}
          onProfile={onProfile}
          onChangePassword={onChangePassword}
          onAccountSettings={onAccountSettings}
          onMyPlans={onMyPlans}
          onMobileCode={onMobileCode}
          onSignOut={onSignOut}
          onJoin={onJoin}
          onSignIn={onSignIn}
          guest={chrome.guest}
        />
      )}
    </header>
  );
}
